export class MiniMap {
  private container: HTMLElement
  private startingCenter: mapboxgl.LngLatLike = [129.434, 30.17]
  private map!: mapboxgl.Map
  /** оборот раз в столько секунд */
  private secondsPerRevolution = 30
  /** крутит ли пользователь карту */
  private userInteracting = false
  /** самовращение */
  private spin = true
  /** сенсорный ли экран */
  private isTouch = matchMedia('(pointer: coarse)').matches

  constructor(container: HTMLElement) {
    this.container = container
    window.addEventListener('load', () => this.initialize())
  }

  /** Асинхронная инициализация */
  private async initialize() {
    this.map = await this.createMap()
    this.container.querySelector('.mapboxgl-control-container')?.remove()
    this.configureMap(this.map)
    this.addCityPoint()
    if (!this.spin) return
    new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.spin = entry.isIntersecting
        this.spinGlobe()
      })
    }).observe(this.container)
  }

  /** Создать экземпляр карты */
  private async createMap(): Promise<mapboxgl.Map> {
    const mapboxgl = await import('mapbox-gl')
    await import('mapbox-gl/dist/mapbox-gl.css')
    const map = new mapboxgl.Map({
      accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
      antialias: true,
      container: this.container,
      center: this.startingCenter,
      interactive: !this.isTouch,
      projection: 'globe',
      style: 'mapbox://styles/mapbox/outdoors-v12',
      zoom: 0.3,
      scrollZoom: false,
      attributionControl: false
    })
    // стили нужно применять сразу после создания карты
    map.on('style.load', () => {
      map.setFog({
        'star-intensity': 0.3,
        'horizon-blend': 0.03,
        'space-color': 'transparent',
        'high-color': '#5aa9e2'
      })
    })
    await new Promise(resolve => map.once('load', resolve))
    return map
  }

  /** Настройки карты и взаимодействия с картой  */
  private configureMap(map: mapboxgl.Map) {
    map.getCanvasContainer().style.cursor = 'auto'
    map.on('mousedown', () => {
      this.userInteracting = true
    })
    map.on('mouseup', () => {
      this.userInteracting = false
      this.spinGlobe()
    })
    map.on('dragend', () => {
      this.userInteracting = false
      this.spinGlobe()
    })
    map.on('pitchend', () => {
      this.userInteracting = false
      this.spinGlobe()
    })
    map.on('rotateend', () => {
      this.userInteracting = false
      this.spinGlobe()
    })
    map.on('moveend', this.spinGlobe.bind(this))
  }

  /** Функция вращения */
  private spinGlobe() {
    if (this.spin && !this.userInteracting) {
      let distancePerSecond = 360 / this.secondsPerRevolution
      const center = this.map.getCenter()
      center.lng -= distancePerSecond
      this.map.easeTo({ center, duration: 1000, easing: (n: any) => n }) // анимация длится 1сек и в конце вызывает событие moveend
    }
  }

  /** Добавить точку с городом */
  private async addCityPoint(city: string = 'Краснодар') {
    const response = await fetch(
      `https://api.mapbox.com/search/geocode/v6/forward?q=${city}&access_token=${import.meta.env.VITE_MAPBOX_TOKEN}`
    )
    const data = await response.json()
    const coordinates = data.features[0].geometry.coordinates as [number, number]

    this.map.addSource('city', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates
        },
        properties: {
          city
        }
      }
    })

    this.map.addLayer({
      id: 'city',
      type: 'circle',
      source: 'city',
      paint: {
        'circle-radius': 3,
        'circle-color': '#ff4000'
      }
    })

    this.map.addLayer({
      id: 'city-label',
      type: 'symbol',
      source: 'city',
      layout: {
        'text-field': ['get', 'city'],
        'text-offset': [0, -2],
        'text-anchor': 'top',
        'text-size': 12
      },
      paint: {
        'text-color': '#000000'
      }
    })
  }
}
